import { createFileRoute, useSearch } from "@tanstack/react-router";
import { useId } from "react";

import { OrdersView } from "~/components/reports/transaction-analysis/views/orders-view";
import { PaymentsView } from "~/components/reports/transaction-analysis/views/payments-view";
import { ProductMixView } from "~/components/reports/transaction-analysis/views/product-mix-view";
import { RefundsView } from "~/components/reports/transaction-analysis/views/refunds-view";
import { TaxesView } from "~/components/reports/transaction-analysis/views/taxes-view";
import { VoidsView } from "~/components/reports/transaction-analysis/views/voids-view";
import { enumIncludes, parseEnumValue } from "~/lib/parse-enum-value";
import {
  useSearchParamsDateRange,
  requireDateRange,
} from "~/utils/search-params-date-range";

const TX_ANALYSIS_GROUP_BY = [
  "orders",
  "payments",
  "refunds",
  "voids",
  "taxes",
  "product-mix",
] as const;
type GroupByType = (typeof TX_ANALYSIS_GROUP_BY)[number];
const isTxAnalysisGroupBy = enumIncludes(TX_ANALYSIS_GROUP_BY);

export const Route = createFileRoute(
  "/_authenticated/reports/transaction-analysis"
)({
  component: TransactionAnalysisReport,
});

function TransactionAnalysisReport() {
  const searchParams = useSearch({ strict: false });
  const locationId = searchParams.location;
  const groupBy = parseEnumValue(
    searchParams.groupBy,
    isTxAnalysisGroupBy,
    "orders"
  );

  const [dateRange] = useSearchParamsDateRange();

  if (!locationId) {
    return (
      <div className="flex h-64 items-center justify-center">
        <div className="text-center">
          <h3 className="subtitle text-foreground mb-2">Location Required</h3>
          <p className="body text-muted-foreground">
            Please select a location from the filters above to view Transaction
            Analysis.
          </p>
        </div>
      </div>
    );
  }

  if (!(dateRange.from && dateRange.to)) {
    return (
      <div className="flex h-64 items-center justify-center">
        <div className="text-center">
          <h3 className="subtitle text-foreground mb-2">Date Range Required</h3>
          <p className="body text-muted-foreground">
            Please select a date range from the filters above.
          </p>
        </div>
      </div>
    );
  }

  const typedDateRange = requireDateRange(dateRange);
  if (!typedDateRange) {
    return null;
  }

  return (
    <TransactionAnalysisContent
      dateRange={typedDateRange}
      groupBy={groupBy}
      locationId={locationId}
    />
  );
}

const TransactionAnalysisContent = ({
  locationId,
  groupBy,
  dateRange,
}: {
  locationId: string;
  groupBy: GroupByType;
  dateRange: { from: Date; to: Date };
}) => {
  const tableId = useId();

  const getTitle = () => {
    switch (groupBy) {
      case "orders":
        return "Order Analysis";
      case "payments":
        return "Payment Analysis";
      case "refunds":
        return "Refund Analysis";
      case "voids":
        return "Void Analysis";
      case "taxes":
        return "Tax Analysis";
      case "product-mix":
        return "Product Mix Analysis";
      default:
        return "Transaction Analysis";
    }
  };

  const getDescription = () => {
    switch (groupBy) {
      case "orders":
        return "Detailed breakdown of all completed orders with billing information";
      case "payments":
        return "Payment method and tender type analysis";
      case "refunds":
        return "Detailed refund transactions and methods";
      case "voids":
        return "Voided items with reasons and amounts";
      case "taxes":
        return "Tax collection breakdown by category and rate";
      case "product-mix":
        return "Complete product mix analysis with refunds and financial breakdown";
      default:
        return "Transaction analysis data";
    }
  };

  const renderView = () => {
    switch (groupBy) {
      case "orders":
        return (
          <OrdersView
            dateRange={dateRange}
            locationId={locationId}
            tableId={tableId}
          />
        );
      case "payments":
        return (
          <PaymentsView
            dateRange={dateRange}
            locationId={locationId}
            tableId={tableId}
          />
        );
      case "refunds":
        return (
          <RefundsView
            dateRange={dateRange}
            locationId={locationId}
            tableId={tableId}
          />
        );
      case "voids":
        return (
          <VoidsView
            dateRange={dateRange}
            locationId={locationId}
            tableId={tableId}
          />
        );
      case "taxes":
        return (
          <TaxesView
            dateRange={dateRange}
            locationId={locationId}
            tableId={tableId}
          />
        );
      case "product-mix":
        return (
          <ProductMixView
            dateRange={dateRange}
            locationId={locationId}
            tableId={tableId}
          />
        );
      default:
        return null;
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="subtitle text-foreground mb-2">{getTitle()}</h2>
        <p className="body text-foreground">{getDescription()}</p>
      </div>

      {renderView()}
    </div>
  );
};
